import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Center,
  Loader,
  Modal,
  Stack,
} from '@mantine/core';
import { FaPlay } from 'react-icons/fa';
import { usePersonalVideoUrl } from '@hooks/usePersonalVideoUrl';
import { useVideoDuration } from '@hooks/useVideoDuration';

interface VideoIntroPlayerProps {
  personalVideoId: number | null | undefined;
  title?: string;
}

const VIDEO_STYLE: React.CSSProperties = {
  width: '100%',
  maxHeight: 520,
  borderRadius: 8,
  display: 'block',
  backgroundColor: '#000',
};

/**
 * A centered play-button overlay (expects a positioned parent) that opens the
 * worker's video intro in a modal. The presigned URL is fetched lazily — only
 * once the modal is opened.
 */
function VideoIntroPlayer({
  personalVideoId,
  title = 'Video Intro',
}: VideoIntroPlayerProps) {
  const [opened, setOpened] = useState(false);
  const { videoRef, onLoadedMetadata } = useVideoDuration();
  const { data, isLoading, isError, refetch } = usePersonalVideoUrl(
    opened ? personalVideoId : null
  );
  const videoUrl = data?.url ?? null;

  if (!personalVideoId) return null;

  return (
    <>
      <Center
        pos="absolute"
        inset={0}
        style={{ pointerEvents: 'none', zIndex: 2 }}
      >
        <ActionIcon
          aria-label="Play video intro"
          radius="xl"
          size="xl"
          onClick={(e) => {
            e.stopPropagation();
            setOpened(true);
          }}
          style={{ pointerEvents: 'auto' }}
          styles={{
            root: {
              backgroundColor: 'var(--yellow)',
              color: '#000',
            },
          }}
        >
          <FaPlay size={18} />
        </ActionIcon>
      </Center>

      {opened && (
        <Modal
          opened
          onClose={() => setOpened(false)}
          title={title}
          centered
          size="lg"
          zIndex={3001}
        >
          {isLoading && (
            <Center p="xl">
              <Loader />
            </Center>
          )}

          {isError && !isLoading && (
            <Stack gap="sm">
              <Alert color="red" title="Couldn't load video">
                We couldn&apos;t load this video intro. Please try again.
              </Alert>
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </Stack>
          )}

          {videoUrl && (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- profile intro, no captions
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              autoPlay
              onLoadedMetadata={onLoadedMetadata}
              onError={() => refetch()}
              style={VIDEO_STYLE}
            />
          )}
        </Modal>
      )}
    </>
  );
}

export default VideoIntroPlayer;
